import React, { FC, PropsWithChildren } from 'react'
import { Gateway, GatewayState } from 'src/api'
import { TabGroupFilter, PropsInjector, DataLoad } from 'src/components'
import { useGatewaysListRequest } from 'src/hooks/api'
import { useQueryParamsState } from 'src/hooks/ui'
import { transformGatewaysState } from 'src/hooks/api/helpers'
import { SensorStateList, SearchParamsKeys } from 'src/enums'
import {
  AllGatewayList,
  NewGatewayList,
  AvailableGatewayList,
  AssignedGatewayList,
  InUseGatewayList,
  LostGatewayList,
} from './gatewayList'

const getStatusList = ({
  data,
  count,
  isLoading,
  states,
  refetchGatewayList
}: {
  data: Gateway[]
  count: number
  isLoading: boolean
  states: GatewayState[]
  refetchGatewayList: () => void
}) => [
  {
    status: SensorStateList.All,
    content: <AllGatewayList data={data} count={count} isLoading={isLoading} states={states} refetchGatewayList={refetchGatewayList} />
  },
  {
    status: SensorStateList.New,
    content: <NewGatewayList data={data} count={count} isLoading={isLoading} states={states} refetchGatewayList={refetchGatewayList} />
  },
  {
    status: SensorStateList.Available,
    content: <AvailableGatewayList data={data} count={count} isLoading={isLoading} states={states} refetchGatewayList={refetchGatewayList} />
  },
  {
    status: SensorStateList.Assigned,
    content: <AssignedGatewayList data={data} count={count} isLoading={isLoading} states={states} refetchGatewayList={refetchGatewayList} />
  },
  {
    status: SensorStateList.InUse,
    content: <InUseGatewayList data={data} count={count} isLoading={isLoading} states={states} refetchGatewayList={refetchGatewayList} />
  },
  {
    status: SensorStateList.Lost,
    content: <LostGatewayList data={data} count={count} isLoading={isLoading} states={states} refetchGatewayList={refetchGatewayList} />
  }
]

interface GatewayTabsProps {
  data?: Gateway[]
  count?: number
  isLoading?: boolean
  refetchGatewayList?: () => void
  states?: GatewayState[]
}

const GatewayTabs: FC<GatewayTabsProps> = ({ data = [], count = 0, isLoading = false, refetchGatewayList = () => {}, states = [] }) => {
  return (
    <TabGroupFilter
      statusList={getStatusList({
        data,
        count,
        isLoading,
        refetchGatewayList,
        states
      })}
      name={SearchParamsKeys.State}
      paramsToRemove={[
        SearchParamsKeys.Page,
        SearchParamsKeys.RowsPerPage,
        SearchParamsKeys.Sort,
      ]}
    />
  )
}

interface GatewayListLoaderProps {
  states: GatewayState[]
}

const GatewayListLoader: FC<PropsWithChildren<GatewayListLoaderProps>> = ({ states, children }) => {
  const state = useQueryParamsState()

  const { data, error, isInitialLoading, isError, refetch } = useGatewaysListRequest({
    params: {
      ...state,
    },
    paramsTransform: [
      transformGatewaysState(states || []),
    ],
  })

  const props = {
    data: data?.rows || [],
    count: data?.count || 0,
    isLoading: isInitialLoading,
    states,
    refetchGatewayList: refetch
  }

  return (
    <DataLoad isLoading={false} isError={isError} errorMessage={error?.message}>
      <PropsInjector props={props}>{children}</PropsInjector>
    </DataLoad>
  )
}

interface GatewayListProps {
  states?: GatewayState[]
}

export const GatewayList: FC<GatewayListProps> = ({ states = [] }) => {
  return (
    <GatewayListLoader states={states}>
      <GatewayTabs />
    </GatewayListLoader>
  )
}
